import { Box, Stack, SxProps } from '@mui/material';
import { t } from 'i18next';
import { MouseEvent } from 'react';
import { Outlet } from 'react-router-dom';
import { BaseController, BaseState } from '../../../../common/controllers/BaseController';
import { Severity } from '../../../../common/enums/Severity';
import { WithRouterProps, withRouter } from '../../../../common/higher-order-components/with-router';
import { InstancesTableSkeleton } from '../../../../common/instances/components/skeletons/InstancesTableSkeleton';
import { Instance } from '../../../../common/instances/models/Instance';
import { PaginationResult } from '../../../../common/types/pagination';
import { UrlHelper } from '../../../../common/utils/UrlHelper';
import { bind } from '../../../../common/utils/decorators';
import { theme } from '../../../../config/theme/theme';
import { PATHS } from '../../../../routing/routes';
import { ColumnPlaceholder } from '../../common/components/ColumnPlaceholder';
import { SeverityFilter } from '../../common/components/SeverityFilter';
import { InitialInstancePayload, ReportIssuesContextType } from '../../common/contexts/ReportIssuesContext';
import { IssueDetails } from '../components/IssueDetails';
import { Issue } from '../models/Issue';
import { InstanceCounters } from '../models/valueObject/InstanceCounters';
import { DEFAULT_INSTANCES_LIST_PAGE_SIZE, ReportIssueRepository } from '../services/ReportIssueRepository';
import { InstancesListController } from './InstancesListController';

type Props = WithRouterProps<ReportIssuesContextType> & {};

type State = BaseState & {
  initialLoad: boolean;
  loadingIssue: boolean;
};

// Custom style for the instance drawer for narrow screens (below the `xl` breakpoint).
// The drawer container behaves like a simple `div` above the breakpoint
const INSTANCE_DRAWER_SX: SxProps = {
  [theme.breakpoints.down('xl')]: {
    position: 'fixed',
    p: 2,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: '100',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    overflowY: 'scroll',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'end',
  },
};

// Custom style for the Instance content inside the open drawer on narrow screens
// The drawer content behaves like a simple `div` above the breakpoint
const INSTANCE_IN_DRAWER_SX: SxProps = { [theme.breakpoints.down('xl')]: { width: '100%', maxWidth: '768px', cursor: 'auto' } };

const INSTANCE_BACKDROP_CLASSNAME = 'instance-drawer-backdrop';

class Controller extends BaseController<Props, State> {
  private readonly repository: ReportIssueRepository;

  private issue: Issue | null;
  private initialInstances: PaginationResult<Instance> | null;
  private instanceSeverityFilters: SeverityFilter[] | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.repository = new ReportIssueRepository();

    this.issue = null;
    this.initialInstances = null;
    this.instanceSeverityFilters = null;

    this.state = { ...super.getBaseState(), initialLoad: true, loadingIssue: true };
  }

  componentDidMount(): void {
    super.componentDidMount();

    window.addEventListener('keydown', this.onEscKeydown);

    if (this.props.context.initialInstanceLoaded) {
      // When the component mounts, only fetch the Issue if the Instance column
      // is ready (not displayed or loaded). If an Instance has been loaded,
      // the Issue's `initialInstances` must be fetched and centered around the
      // selected instance
      this.fetchIssue(this.props.context.initialInstance);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    if (!prevProps.context.initialInstanceLoaded && this.props.context.initialInstanceLoaded) {
      // Handle the case where the Instance was not ready at component mount
      this.fetchIssue(this.props.context.initialInstance);
    }

    if (Number.parseId(this.props.params['issueId']) !== Number.parseId(prevProps.params['issueId'])) {
      // The Issue has changed, the controller is not re-mounted.
      // Handle the refresh of the new selected Issue
      this.fetchIssue();
    }
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();

    window.removeEventListener('keydown', this.onEscKeydown);
  }

  render() {
    const { initialIssuesLoaded, initialIssueLoaded, initialInstanceLoaded, includeFilteredItems } = this.props.context;
    const { loadingIssue, initialLoad } = this.state;
    const { reportId, issueNumber, instanceNumber } = this.getPathParams();

    // Skeletons should be displayed as long as the Issue has not been fetched for the first time
    // and as long as all columns of the layout are loading
    const showSkeleton = initialLoad || !(initialIssuesLoaded && initialIssueLoaded && initialInstanceLoaded);

    // For skeleton display purposes, consider that the Issue has instances as long as it's loading.
    // Once it's loaded, only display Instances if they are available. This works for the most common
    // case where each Issue has at least one Instance (note that this may not apply for DISABLED / N/A issues)
    const hasInstances = showSkeleton || !!this.initialInstances?.data.length;

    return this.renderBase(
      <Stack className="overflowing-box-shadow" gap={2}>
        <IssueDetails issue={this.issue} loading={loadingIssue} initialLoad={showSkeleton} />
        <Box sx={{ display: 'flex', flex: 1 }}>
          {hasInstances ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', columnGap: 2, width: 1 }}>
              <Box sx={{ gridColumn: { xl: 'span 9', xs: 'span 24' } }}>
                {showSkeleton ? (
                  <InstancesTableSkeleton rows={DEFAULT_INSTANCES_LIST_PAGE_SIZE} />
                ) : (
                  <InstancesListController
                    // Re-mount the Instances list when a new Issue is loaded
                    // to load the new prefilled initial instances
                    key={`instances_${this.issue?.getNumber() ?? issueNumber}`}
                    reportId={reportId}
                    issueNumber={issueNumber}
                    selectedInstanceNumber={instanceNumber}
                    initialInstances={this.initialInstances}
                    parentLoading={showSkeleton || loadingIssue}
                    includeFilteredItems={includeFilteredItems}
                    onInstanceFilteredOut={this.closeInstance}
                    severityFilters={this.instanceSeverityFilters}
                  />
                )}
              </Box>
              <Box sx={{ gridColumn: { xl: 'span 15' }, display: { xl: 'block', xs: instanceNumber ? 'block' : 'none' } }}>
                {instanceNumber ? (
                  <Box className={INSTANCE_BACKDROP_CLASSNAME} onClick={this.onInstanceBackdropClick} sx={INSTANCE_DRAWER_SX}>
                    <Box sx={INSTANCE_IN_DRAWER_SX}>
                      <Outlet context={this.props.context} />
                    </Box>
                  </Box>
                ) : (
                  <ColumnPlaceholder
                    title={t('selectInstancePlaceholder.title')}
                    subtitle={t('selectInstancePlaceholder.subtitle')}
                    loading={loadingIssue}
                    initialLoad={showSkeleton}
                  />
                )}
              </Box>
            </Box>
          ) : (
            <ColumnPlaceholder
              title={t('noInstancesPlaceholder.title')}
              subtitle={t('noInstancesPlaceholder.subtitle')}
              loading={loadingIssue}
              initialLoad={showSkeleton}
            />
          )}
        </Box>
      </Stack>
    );
  }

  private getPathParams() {
    const { reportId, issueId: issueNumber } = this.parseNumericIds(['reportId', 'issueId']);
    return {
      reportId,
      issueNumber,
      instanceNumber: Number.parseId(this.props.params['instanceId']),
    };
  }

  @bind
  private async fetchIssue(initialInstance?: InitialInstancePayload) {
    try {
      this.setState({ loadingIssue: true });

      const { reportId, issueNumber } = this.getPathParams();

      const { issue, initialInstances, instanceCounters } = await this.repository.getIssue(
        reportId,
        issueNumber,
        this.props.context.includeFilteredItems,
        initialInstance
      );
      this.issue = issue;
      this.initialInstances = initialInstances;
      this.instanceSeverityFilters = this.buildInstanceSeverityFilters(instanceCounters);

      this.setState({ initialLoad: false, loadingIssue: false });

      this.props.context.onIssueLoaded();
    } catch (e: unknown) {
      this.setState({ initialLoad: false, loadingIssue: false });
      this.handleError(e, { invalidUrl: t('nonExistingIssue') });
    }
  }

  @bind
  private onInstanceBackdropClick(event: MouseEvent<HTMLDivElement>) {
    // This event will be fired on click of the backdrop container and all its content,
    // including the instance controller itself.
    // Do not close the instance unless the click happened on the backdrop.
    if (!(event.target as HTMLDivElement).classList.contains(INSTANCE_BACKDROP_CLASSNAME)) return;

    this.closeInstance();
  }

  @bind
  private onEscKeydown(event: KeyboardEvent) {
    const { instanceNumber } = this.getPathParams();
    if (!instanceNumber || event.key !== 'Escape') return;

    this.closeInstance();
  }

  @bind
  private closeInstance() {
    const { reportId, issueNumber } = this.getPathParams();

    this.props.navigate(
      UrlHelper.generatePath(PATHS.REPORT_ISSUE, { reportId: reportId.toString(), issueId: issueNumber.toString() }, { keepQueryParams: true })
    );
  }

  private buildInstanceSeverityFilters(counters: InstanceCounters) {
    return [
      { severity: Severity.HIGH, count: counters.getHigh() },
      { severity: Severity.MED, count: counters.getMedium() },
      { severity: Severity.LOW, count: counters.getLow() },
      { severity: Severity.PASSED, count: counters.getPassed() },
    ];
  }
}

export const ReportIssueController = withRouter<ReportIssuesContextType>(Controller);
